import { NormalizedRoute, NormalizedRoutes, RouteParams } from './route-types'

function isRoutesApiEnabled(): boolean {
  return document.getElementById('root')?.dataset?.useRoutesApi === 'true'
}

export async function fetchRoute(params: RouteParams): Promise<NormalizedRoutes> {
  return isRoutesApiEnabled() ? await fetchViaRoutes(params) : await fetchViaDirections(params)
}

// --- Legacy path: wraps existing DirectionsService ---

async function fetchViaDirections(params: RouteParams): Promise<NormalizedRoutes> {
  return new Promise((resolve, reject) => {
    let directionService: google.maps.DirectionsService
    try {
      directionService = new google.maps.DirectionsService()
    } catch (ex) {
      console.error('Failed to load Google Directions Service')
      resolve({ routes: [], error: 'Directions Service unavailable' })
      return
    }

    const req: google.maps.DirectionsRequest = {
      origin: params.origin,
      destination: params.destination,
      travelMode: google.maps.TravelMode.DRIVING,
      unitSystem: google.maps.UnitSystem.METRIC,
      provideRouteAlternatives: true,
      region: 'NZ',
      drivingOptions: {
        departureTime: new Date(Date.now()),
      },
    }

    if (params.waypoints?.length) {
      req.waypoints = params.waypoints.map((wp) => ({
        location: new google.maps.LatLng(wp.lat, wp.lng),
        stopover: true,
      }))
      req.optimizeWaypoints = params.optimizeWaypoints ?? false
      req.provideRouteAlternatives = false
    }

    try {
      directionService.route(req, (res, status) => {
        if (status === google.maps.DirectionsStatus.OK && res) {
          const routes = res.routes.map((route) => normalizeDirectionsRoute(route))
          resolve({ routes })
        } else {
          resolve({ routes: [] })
        }
      })
    } catch (ex) {
      resolve({ routes: [] })
    }
  })
}

function normalizeDirectionsRoute(route: google.maps.DirectionsRoute): NormalizedRoute {
  const legs = route.legs
  const durationSeconds = legs.reduce((sum, leg) => {
    const value = leg.duration_in_traffic?.value ?? leg.duration?.value ?? 0
    return sum + value
  }, 0)
  const staticDurationSeconds = legs.reduce((sum, leg) => {
    return sum + (leg.duration?.value ?? 0)
  }, 0)
  const distanceMeters = legs.reduce((sum, leg) => {
    return sum + (leg.distance?.value ?? 0)
  }, 0)

  const decodedPath = route.overview_path.map((p) => ({
    lat: p.lat(),
    lng: p.lng(),
  }))

  const encodedPolyline =
    typeof route.overview_polyline === 'string'
      ? route.overview_polyline
      : (route.overview_polyline as any)?.points ?? encodePathFallback(decodedPath)

  return {
    encodedPolyline,
    decodedPath,
    distanceMeters,
    durationSeconds,
    staticDurationSeconds,
    startLocation: {
      lat: legs[0].start_location.lat(),
      lng: legs[0].start_location.lng(),
    },
    endLocation: {
      lat: legs[legs.length - 1].end_location.lat(),
      lng: legs[legs.length - 1].end_location.lng(),
    },
    description: (route as any).summary || 'Unnamed route',
    hasTolls: directionsRouteHasTolls(route),
  }
}

function directionsRouteHasTolls(route: google.maps.DirectionsRoute): boolean {
  const allInstructions = route.legs.map((leg) => leg.steps.map((step) => step.instructions))
  return JSON.stringify(allInstructions).toLowerCase().indexOf('toll road') >= 0
}

function encodePathFallback(path: google.maps.LatLngLiteral[]): string {
  try {
    const latLngs = path.map((p) => new google.maps.LatLng(p.lat, p.lng))
    return google.maps.geometry.encoding.encodePath(latLngs)
  } catch {
    // Empty string silently breaks the Static Maps URL and My Journeys save
    // POST — warn so the failure is visible if the geometry library fails
    // to load.
    console.warn('encodedPolyline unavailable: geometry library not loaded')
    return ''
  }
}

// --- New path: Routes API via JS SDK ---
//
// Note: @types/google.maps 3.58.x does not yet cover google.maps.routes.*.
// The minimal types below mirror the documented surface for
// google.maps.routes.Route.computeRoutes and the Route result shape.
// See: https://developers.google.com/maps/documentation/javascript/reference/route
//      https://developers.google.com/maps/documentation/javascript/routes/get-a-route

type RoutesComputeRequest = {
  origin: google.maps.LatLngLiteral | string
  destination: google.maps.LatLngLiteral | string
  travelMode: google.maps.TravelMode
  routingPreference?: 'TRAFFIC_AWARE' | 'TRAFFIC_AWARE_OPTIMAL' | 'TRAFFIC_UNAWARE'
  computeAlternativeRoutes?: boolean
  intermediates?: google.maps.LatLngLiteral[]
  optimizeWaypointOrder?: boolean
  region?: string // ccTLD, lowercase
  fields: string[] // required
}

type RoutesApiRoute = {
  durationMillis?: number
  staticDurationMillis?: number
  distanceMeters?: number
  path?: Array<google.maps.LatLngLiteral & { altitude?: number }>
  legs?: Array<{ startLocation?: unknown; endLocation?: unknown; path?: unknown }>
  description?: string
  travelAdvisory?: { tollInfo?: unknown }
  // Live Route instances returned by computeRoutes carry rendering helpers.
  // Shape mirrors the documented SDK method; the library loader returns real
  // Route instances, not plain objects.
  createPolylines?: (options?: RoutePolylineOptions) => google.maps.Polyline[]
}

export type RoutePolylineOptions = {
  polylineOptions?: google.maps.PolylineOptions
}

// Live Route instances are stashed here so that rendering code — which runs
// after normalization — can call createPolylines() on them. Keyed by the
// caller-supplied cacheKey (typically the journey's locationHash).
//
// This side-store is migration-era scaffolding: it bridges the gap between
// the NormalizedRoute shape (plain data, methods stripped) and the SDK-method
// call surface rendering needs. It should be retired once the legacy
// Directions API path is removed and NormalizedRoute can be replaced by the
// live Route instance directly. See docs/ROUTES_API_POST_MIGRATION.md for
// the recommended cleanup plan.
const liveRoutes = new Map<string, RoutesApiRoute[]>()

export function getLiveRoutes(key: string): RoutesApiRoute[] | undefined {
  return liveRoutes.get(key)
}

export function clearLiveRoutes(key?: string) {
  if (key === undefined) {
    liveRoutes.clear()
  } else {
    liveRoutes.delete(key)
  }
}

type RoutesLibrary = {
  Route: {
    computeRoutes(request: RoutesComputeRequest): Promise<{ routes: RoutesApiRoute[] }>
  }
}

export const ROUTES_FIELD_MASK = [
  'durationMillis',
  'staticDurationMillis',
  'distanceMeters',
  'path',
  'legs',
  'description',
  'travelAdvisory',
]

async function fetchViaRoutes(params: RouteParams): Promise<NormalizedRoutes> {
  try {
    const request: RoutesComputeRequest = {
      origin: params.origin,
      destination: params.destination,
      travelMode: google.maps.TravelMode.DRIVING,
      routingPreference: 'TRAFFIC_AWARE_OPTIMAL',
      computeAlternativeRoutes: !params.waypoints?.length,
      region: 'nz', // Routes API ccTLD region code is lowercase
      fields: ROUTES_FIELD_MASK,
    }

    if (params.waypoints?.length) {
      request.intermediates = params.waypoints
      request.optimizeWaypointOrder = params.optimizeWaypoints ?? false
    }

    const { Route } = (await google.maps.importLibrary('routes')) as unknown as RoutesLibrary
    const { routes: routeResults } = await Route.computeRoutes(request)

    if (!routeResults?.length) {
      if (params.cacheKey) clearLiveRoutes(params.cacheKey)
      return { routes: [] }
    }

    if (params.cacheKey) liveRoutes.set(params.cacheKey, routeResults)

    const routes = routeResults.map((route) => normalizeRoutesApiRoute(route))
    return { routes }
  } catch (ex) {
    const message = ex instanceof Error ? ex.message : 'Unknown Routes API error'
    console.error('Failed to compute routes via Routes API', ex)
    return { routes: [], error: message }
  }
}

function normalizeRoutesApiRoute(route: RoutesApiRoute): NormalizedRoute {
  const durationSeconds = Math.round((route.durationMillis ?? 0) / 1000)
  const staticDurationSeconds = Math.round((route.staticDurationMillis ?? 0) / 1000)
  const distanceMeters = route.distanceMeters ?? 0

  const decodedPath: google.maps.LatLngLiteral[] = (route.path ?? []).map((p: any) => ({
    lat: typeof p.lat === 'function' ? p.lat() : p.lat,
    lng: typeof p.lng === 'function' ? p.lng() : p.lng,
  }))

  // Re-encode the overview path so downstream consumers (My Journeys save,
  // Static Maps URL) get a valid encoded polyline. Route.path is already a
  // simplified overview, comparable to the Directions API's overview_path.
  const encodedPolyline = encodePathFallback(decodedPath)

  const legs = route.legs ?? []
  const firstLeg = legs[0]
  const lastLeg = legs[legs.length - 1] ?? firstLeg

  const startLocation = extractLatLng(firstLeg?.startLocation) ?? decodedPath[0] ?? { lat: 0, lng: 0 }
  const endLocation =
    extractLatLng(lastLeg?.endLocation) ?? decodedPath[decodedPath.length - 1] ?? { lat: 0, lng: 0 }

  const hasTolls = !!route.travelAdvisory?.tollInfo
  const description = route.description || 'Unnamed route'

  return {
    encodedPolyline,
    decodedPath,
    distanceMeters,
    durationSeconds,
    staticDurationSeconds,
    startLocation,
    endLocation,
    description,
    hasTolls,
  }
}

function extractLatLng(
  location: any | undefined,
): google.maps.LatLngLiteral | null {
  if (!location) return null
  // DirectionalLocation extends LatLngAltitude — may be a LatLng-like or a plain object
  if (typeof location.lat === 'function') {
    return { lat: location.lat(), lng: location.lng() }
  }
  if (typeof location.lat === 'number') {
    return { lat: location.lat, lng: location.lng }
  }
  // Nested latLng property (DirectionalLocation wrapping)
  if (location.latLng) {
    return extractLatLng(location.latLng)
  }
  return null
}
